import React, { useState } from "react";
import { Text } from "src/sudokit/components/Text";
import { Textarea } from "src/sudokit/components/Textarea";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { DEFAULT_IMAGE_GENERATION_MODEL_ID } from "~shared/imageGenerationModels";
import { ALLOWED_REFERENCE_IMAGE_MIME_TYPES } from "../../components/EntityImage/lib/constants";
import Field from "./shared/Field";
import ModelSelect, { modelSupportsReferenceImage } from "./shared/ModelSelect";
import PanelLayout from "./shared/PanelLayout";
import ReferenceImageField from "./shared/ReferenceImageField";
import ResultPanel from "./shared/ResultPanel";
import StylePresetEditor from "./shared/StylePresetEditor";
import { useImageGeneration } from "./shared/useImageGeneration";
import { useReferenceImage } from "./shared/useReferenceImage";
import { useStylePresets } from "./shared/useStylePresets";
import { CUSTOM_STYLE_PRESET_ID } from "./shared/types";

const PRESETS_ENDPOINT = "/api/admin/imageGenTest/character/presets";
const GENERATE_ENDPOINT = "/api/admin/imageGenTest/character/generate";

const CharacterPanel: React.FC = () => {
  const [physicalDescription, setPhysicalDescription] = useState("");
  const [pronouns, setPronouns] = useState("");
  const [userDescription, setUserDescription] = useState("");
  const [genre, setGenre] = useState("");
  const [style, setStyle] = useState("");
  const [model, setModel] = useState<string>(DEFAULT_IMAGE_GENERATION_MODEL_ID);

  const presets = useStylePresets(PRESETS_ENDPOINT);
  const reference = useReferenceImage(ALLOWED_REFERENCE_IMAGE_MIME_TYPES);
  const { loading, imageUrl, result, error, generate } = useImageGeneration();

  const canGenerate =
    Boolean(physicalDescription.trim() || userDescription.trim()) &&
    Boolean(presets.stylePresetId) &&
    (presets.stylePresetId !== CUSTOM_STYLE_PRESET_ID || Boolean(presets.stylePresetText.trim()));

  const handleGenerate = () =>
    generate(GENERATE_ENDPOINT, {
      physicalDescription: physicalDescription || undefined,
      pronouns: pronouns || undefined,
      userDescription: userDescription || undefined,
      genre: genre || undefined,
      style: style || undefined,
      stylePresetId: presets.stylePresetId,
      stylePresetText: !presets.isStylePresetTextDefault ? presets.stylePresetText || undefined : undefined,
      referenceImage: modelSupportsReferenceImage(model) ? reference.referenceImage ?? undefined : undefined,
      model,
    });

  const conceptPreview = result
    ? [result.subject, result.mood ? `Mood & palette: ${result.mood}` : null].filter(Boolean).join("\n\n")
    : null;

  return (
    <PanelLayout
      result={<ResultPanel loading={loading} imageUrl={imageUrl} result={result} downloadName="character-avatar-test.png" aspectRatio="1:1" conceptPreview={conceptPreview} />}
    >
      <div className="flex flex-col gap-3 w-full text-left">
        <Text size="sm" weight="bold">
          Story Bible
        </Text>
        <Field id="physicalDescription" label="Physical Description" value={physicalDescription} onChange={setPhysicalDescription} multiline minRows={4} placeholder="e.g. Woman in her late 20s, wavy auburn hair, green eyes, warm confident expression" />
        <Field id="pronouns" label="Pronouns" value={pronouns} onChange={setPronouns} optional placeholder="e.g. she/her" />
        <Field id="genre" label="Genre" value={genre} onChange={setGenre} optional placeholder="e.g. dark romance" />
        <Field id="style" label="Style" value={style} onChange={setStyle} optional placeholder="e.g. lyrical, fast-paced" />
        <Text size="xs" color="grey-1">
          Genre and Style feed the Mood &amp; Palette line the text LLM generates — they don't change the art-style wording itself.
        </Text>
      </div>

      <div className="flex flex-col gap-2 w-full text-left border-t border-app-menu-color-hover pt-6">
        <label htmlFor="userDescription" className="text-sm font-medium">
          User-Described Look <span className="text-app-grey-1 font-normal">(optional)</span>
        </label>
        <Textarea mode="default" id="userDescription" value={userDescription} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setUserDescription(e.target.value)} placeholder="Freeform look text a user typed into the avatar generator UI" minRows={3} />
        <Text size="xs" color="grey-1">
          At least one of Physical Description or User-Described Look is required.
        </Text>
      </div>

      <StylePresetEditor
        variantLabel="Art Style Variant"
        presets={presets.presets}
        presetsLoading={presets.presetsLoading}
        presetsError={presets.presetsError}
        stylePresetId={presets.stylePresetId}
        onStylePresetIdChange={presets.setStylePresetId}
        text={presets.stylePresetText}
        onTextChange={presets.setStylePresetText}
        onResetText={presets.resetStylePresetText}
        isTextDefault={presets.isStylePresetTextDefault}
        variantHelpText="Only the chosen variant is generated — production generates all 4."
        promptHelpText="Edit this to iterate on the art-style wording — it's sent as-is with the generated subject/mood."
      />

      {modelSupportsReferenceImage(model) && <ReferenceImageField field={reference} />}

      <ModelSelect value={model} onChange={setModel} />

      <SudoButton type="button" variant="primary" state={loading ? "loading" : "default"} disabled={loading || !canGenerate} onClick={handleGenerate}>
        {loading ? "Generating..." : "Generate"}
      </SudoButton>

      {error && (
        <Text size="sm" color="danger-color">
          {error}
        </Text>
      )}
    </PanelLayout>
  );
};

export default CharacterPanel;
